/**
 * TermsPage
 * ---------
 * Displays TrigoRide's Terms & Conditions inside the app. The remote origin
 * (www.trigoride.com) restricts iframe embedding via CSP, so we go through
 * a backend proxy (`GET /api/terms`) that fetches the HTML server-side,
 * strips <script>/<style>/inline handlers, and returns the readable body.
 * We then inject that HTML into a normal <article> element — no external
 * navigation, no iframe.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ExternalLink, FileText, Loader2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TERMS_URL = "https://www.trigoride.com/terms-et-conditions";

export default function TermsPage() {
  const navigate = useNavigate();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/terms`, { timeout: 15000 });
      setHtml(data.html || "");
    } catch (e) {
      setError("Impossible de charger les termes et conditions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen w-full bg-[#F5F7FA] flex flex-col" data-testid="terms-page">
      {/* Header */}
      <div className="bg-[#0A2540] text-white px-4 sm:px-6 py-4 flex items-center gap-3 shadow-sm sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition shrink-0"
          data-testid="terms-back-btn"
          aria-label="Retour"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FACC15]">TrigoTour</p>
          <h1 className="text-lg sm:text-xl font-extrabold truncate" style={{ fontFamily: "Outfit" }}>
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Termes et conditions
          </h1>
        </div>
        <a
          href={TERMS_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/80 hover:text-[#FACC15] transition"
          data-testid="terms-external-link"
        >
          Version en ligne <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center gap-2 text-[#0A2540] py-16" data-testid="terms-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm font-semibold">Chargement des termes…</span>
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-12" data-testid="terms-error">
            <p className="text-sm text-[#C1453B] font-semibold mb-3">{error}</p>
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[#0A2540] text-white text-sm font-bold hover:bg-[#0A2540]/90 transition"
              data-testid="terms-retry-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Réessayer
            </button>
          </div>
        )}
        {!loading && !error && (
          <article
            className="terms-content bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-4 sm:p-8 text-[#0A2540] leading-relaxed"
            data-testid="terms-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>

      {/* Minimal readable styles for the injected HTML fragment so the
          content looks native, regardless of what the source page used. */}
      <style>{`
        .terms-content h1,.terms-content h2,.terms-content h3{color:#0A2540;font-family:Outfit,Inter,sans-serif;font-weight:800;margin:1.2em 0 .5em}
        .terms-content h1{font-size:1.6rem}
        .terms-content h2{font-size:1.25rem}
        .terms-content h3{font-size:1.05rem}
        .terms-content p,.terms-content li{font-size:.95rem;color:#334155;margin:.5em 0}
        .terms-content ul,.terms-content ol{padding-left:1.4em;margin:.6em 0}
        .terms-content a{color:#0A2540;text-decoration:underline;font-weight:600}
        .terms-content img{max-width:100%;height:auto;border-radius:12px;margin:.75em 0}
        .terms-content section{margin:1em 0}
        .terms-content [class*="header"],.terms-content [class*="footer"],.terms-content [class*="nav"]{display:none !important}
      `}</style>
    </div>
  );
}
